import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    Box,
    Container,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    IconButton,
    TableContainer,
    Grid,
    Stack,
    Chip,
    Divider,
    CircularProgress,
    Alert,
    TablePagination,
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import Layout from "../../Layout";
import { Bar, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js";
import { Edit, RestartAlt, FileDownload, PictureAsPdf, GridOn } from "@mui/icons-material";
import api from "../../api";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
    RISK_LEVELS,
    UNSUPPORTED_VALUE,
    canonicalRisk,
    detailedStatusCategory,
    riskBucket,
} from "../../utils/trackerNormalization";
import {
    buildUniqueSheetNames,
    formatDateInIST,
    formatDateTimeInIST,
    formatPeople,
} from "../../utils/reportExport";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, ChartTooltip, Legend);

const centerTextPlugin = {
    id: "centerText",
    beforeDraw(chart, _args, opts) {
        if (!opts?.text) return;
        const { ctx, chartArea } = chart;
        ctx.save();
        ctx.font = "600 18px system-ui, -apple-system, Segoe UI, Roboto, Arial";
        ctx.fillStyle = "#333";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        const x = (chartArea.left + chartArea.right) / 2;
        const y = (chartArea.top + chartArea.bottom) / 2;
        ctx.fillText(opts.text, x, y);
        ctx.restore();
    },
};
ChartJS.register(centerTextPlugin);

const complianceCategories = [
    "Pending for Review",
    "Pending for Discrepancy",
    "Open",
    "In-process",
    "Can Not Comply",
    "Not Complied",
    UNSUPPORTED_VALUE,
];

const riskLevels = RISK_LEVELS;

const riskColors = { High: "#e53935", Medium: "#fb8c00", Low: "#43a047", [UNSUPPORTED_VALUE]: "#757575" };

const statusColors = {
    "Pending for Review": "default",
    "Pending for Discrepancy": "warning",
    Open: "info",
    "In-process": "warning",
    "Can Not Comply": "error",
    "Not Complied": "error",
    [UNSUPPORTED_VALUE]: "default",
};

const statusFillColors = {
    "Pending for Review": "#90a4ae",
    "Pending for Discrepancy": "#fb8c00",
    Open: "#1e88e5",
    "In-process": "#fb8c00",
    "Can Not Comply": "#e53935",
    "Not Complied": "#8e24aa",
    [UNSUPPORTED_VALUE]: "#616161",
};

const panelSx = { p: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" };

const normalizeRisk = canonicalRisk;
const canonicalStatus = (status) => {
    const category = detailedStatusCategory(status);
    return complianceCategories.includes(category) ? category : UNSUPPORTED_VALUE;
};
const CLOSED_RISK_PROFILE_CATEGORIES = new Set([
    "Complied On Time",
    "Delayed",
    "Delayed Compliance",
    "Delayed Complied",
    "Not Applicable",
]);

const ComplianceReportPage = () => {
    const [rawTrackersByKey, setRawTrackersByKey] = useState({});
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();

    const [selectedCell, setSelectedCell] = useState({ lawArea: null, status: null, risk: null });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const donutRef = useRef(null);
    const riskShareRef = useRef(null);
    const topHighRef = useRef(null);
    const chartToPng = (ref) => {
        try {
            const chart = ref?.current;
            return chart?.toBase64Image?.("image/png", 1.0) || null;
        } catch {
            return null;
        }
    };

    const [exportAnchor, setExportAnchor] = useState(null);
    const openExportMenu = (e) => setExportAnchor(e.currentTarget);
    const closeExportMenu = () => setExportAnchor(null);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setLoadingCompanies(true);
                const { data } = await api.get("detail/userinfo?view=meta");
                const companyNames = Array.isArray(data?.companyDetails?.company_name)
                    ? data.companyDetails.company_name
                    : data?.companyDetails?.company_name
                        ? [data.companyDetails.company_name]
                        : [];
                setCompanies(companyNames);
            } catch (err) {
                setError("Couldn't load your company info.");
            } finally {
                setLoadingCompanies(false);
            }
        };
        fetchCompanies();
    }, []);

    useEffect(() => {
        const fetchReportData = async () => {
            if (companies.length === 0) return;
            try {
                setError("");
                setLoadingReport(true);
                const response = await api.post("reports/riskprofile", {});
                const trackers = response?.data?.trackers;
                const list = Array.isArray(trackers)
                    ? trackers
                    : trackers && typeof trackers === "object"
                        ? Object.values(trackers).flat().filter(Boolean)
                        : [];
                setRawTrackersByKey({ all: list });
            } catch (err) {
                setError("Couldn't load the compliance report.");
            } finally {
                setLoadingReport(false);
            }
        };
        fetchReportData();
    }, [companies]);

    const { lawAreaMap, processedData } = useMemo(() => {
        const all = Object.values(rawTrackersByKey)
            .flat()
            .filter(Boolean)
            .filter((tracker) => !CLOSED_RISK_PROFILE_CATEGORIES.has(detailedStatusCategory(tracker?.status)));
        const byLaw = all.reduce((acc, t) => {
            const lawArea = t?.lawArea || "Unspecified";
            (acc[lawArea] ||= []).push(t);
            return acc;
        }, {});
        const proc = {};
        Object.entries(byLaw).forEach(([lawArea, items]) => {
            proc[lawArea] ||= {};
            complianceCategories.forEach(
                (category) =>
                    (proc[lawArea][category] = {
                        High: 0,
                        Medium: 0,
                        Low: 0,
                        [UNSUPPORTED_VALUE]: 0,
                    })
            );
            items.forEach((t) => {
                const status = canonicalStatus(t?.status);
                const risk = riskBucket(t?.risk);
                proc[lawArea][status][risk] += 1;
            });
        });
        return { lawAreaMap: byLaw, processedData: proc };
    }, [rawTrackersByKey]);

    const kpis = useMemo(() => {
        const lawAreas = Object.keys(processedData);
        let total = 0;
        let high = 0;
        lawAreas.forEach((lawArea) => {
            complianceCategories.forEach((cat) => {
                riskLevels.forEach((r) => (total += processedData[lawArea]?.[cat]?.[r] || 0));
                high += processedData[lawArea]?.[cat]?.High || 0;
            });
        });
        const affected = lawAreas.filter((la) => {
            const count = complianceCategories.reduce(
                (s, cat) => s + riskLevels.reduce((a, r) => a + (processedData[la]?.[cat]?.[r] || 0), 0),
                0
            );
            return count > 0;
        }).length;
        const highPct = total ? Math.round((high / total) * 100) : 0;
        return { total, high, highPct, affected };
    }, [processedData]);

    const openComplianceChartData = useMemo(() => {
        const labels = Object.keys(processedData);
        return {
            labels,
            datasets: riskLevels.map((risk) => ({
                label: `${risk} Risk`,
                data: labels.map((lawArea) =>
                    complianceCategories.reduce(
                        (sum, category) => sum + (processedData[lawArea]?.[category]?.[risk] || 0),
                        0
                    )
                ),
                backgroundColor: riskColors[risk],
                stack: "risk",
            })),
        };
    }, [processedData]);

    const statusTotalsDonutData = useMemo(() => {
        const labels = complianceCategories;
        const data = labels.map((status) =>
            Object.keys(processedData).reduce((sum, lawArea) => {
                const bucket = processedData[lawArea]?.[status] || {};
                return sum + riskLevels.reduce((acc, r) => acc + (bucket[r] || 0), 0);
            }, 0)
        );
        const filtered = labels
            .map((label, i) => ({ label, value: data[i] }))
            .filter((d) => d.value > 0);
        return {
            labels: filtered.map((f) => f.label),
            datasets: [
                {
                    label: "Items",
                    data: filtered.map((f) => f.value),
                    backgroundColor: filtered.map((f) => statusFillColors[f.label] || "#bdbdbd"),
                    borderWidth: 0,
                },
            ],
        };
    }, [processedData]);

    const riskShareChartData = useMemo(() => {
        const labels = Object.keys(processedData);
        const totalsByLaw = labels.map((lawArea) =>
            complianceCategories.reduce(
                (sum, status) => sum + riskLevels.reduce((acc, r) => acc + (processedData[lawArea]?.[status]?.[r] || 0), 0),
                0
            )
        );
        return {
            labels,
            datasets: riskLevels.map((risk) => ({
                label: `${risk} Risk`,
                data: labels.map((lawArea, i) => {
                    const count = complianceCategories.reduce(
                        (sum, status) => sum + (processedData[lawArea]?.[status]?.[risk] || 0),
                        0
                    );
                    const total = totalsByLaw[i] || 1;
                    return Math.round((count / total) * 100);
                }),
                backgroundColor: riskColors[risk],
                stack: "percent",
            })),
        };
    }, [processedData]);

    const topHighRiskChartData = useMemo(() => {
        const entries = Object.entries(processedData).map(([lawArea, byStatus]) => {
            const high = complianceCategories.reduce((sum, status) => sum + (byStatus?.[status]?.High || 0), 0);
            return { lawArea, high };
        });
        const top = entries.sort((a, b) => b.high - a.high).slice(0, 8);
        return {
            labels: top.map((e) => e.lawArea),
            datasets: [{ label: "High Risk", data: top.map((e) => e.high), backgroundColor: riskColors.High }],
        };
    }, [processedData]);

    const setSelectionFromLabel = useCallback((lawArea, status, risk) => {
        setSelectedCell({ lawArea: lawArea ?? null, status: status ?? null, risk: risk ?? null });
        setPage(0);
    }, []);

    const clearSelection = useCallback(() => {
        setSelectedCell({ lawArea: null, status: null, risk: null });
        setPage(0);
    }, []);

    const detailsRows = useMemo(() => {
        if (!selectedCell.lawArea && !selectedCell.status && !selectedCell.risk) return [];
        const sources =
            selectedCell.lawArea ? (lawAreaMap[selectedCell.lawArea] || []) : Object.values(lawAreaMap).flat();
        return sources.filter((t) => {
            const status = canonicalStatus(t?.status);
            const risk = riskBucket(t?.risk);
            const statusMatch = selectedCell.status ? status === selectedCell.status : true;
            const riskMatch = selectedCell.risk ? risk === selectedCell.risk : true;
            const lawMatch = selectedCell.lawArea ? (t?.lawArea || "Unspecified") === selectedCell.lawArea : true;
            return statusMatch && riskMatch && lawMatch;
        });
    }, [lawAreaMap, selectedCell]);

    const handleCellClick = useCallback((lawArea, status, risk) => {
        setSelectedCell((prev) => {
            const same = prev.lawArea === lawArea && prev.status === status && prev.risk === risk;
            return same ? { lawArea: null, status: null, risk: null } : { lawArea, status, risk };
        });
        setPage(0);
    }, []);

    const isSelected = (lawArea, status, risk) =>
        selectedCell.lawArea === lawArea && selectedCell.status === status && selectedCell.risk === risk;

    const loading = loadingCompanies || loadingReport;
    const hasData = Object.keys(processedData).length > 0;

    const basePlugins = {
        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10 } },
        layout: { padding: 8 },
    };

    const mapTrackerToRow = (t) => ({
        UIN: t?.uniqueIdentifier || "",
        Company: companies.join(", ") || "",
        Location: t?.location || "",
        "Compliance Activity": t?.complianceActivity || "",
        "Law Area": t?.lawArea || "",
        Description: t?.description || "",
        Category: Array.isArray(t?.category) ? t.category.join(", ") : t?.category || "",
        "Act or Rule": t?.actOrRule || "",
        Periodicity: t?.periodicity || "",
        Consequence: t?.consequence || "",
        "Source URL": t?.sourceURL || "",
        "Due Date (IST)": formatDateInIST(t?.dueDate),
        "Completion Date (IST)": formatDateInIST(t?.completionDate),
        Risk: normalizeRisk(t?.risk),
        Status: canonicalStatus(t?.status) || t?.status || "",
        "Raw Risk": t?.risk || "",
        "Raw Status": t?.status || "",
        "Proof Submitted": t?.proof ? "Yes" : "No",
        "Proof Not Required": t?.proofNotRequired ? "Yes" : "No",
        "Tracker Owner(s)": formatPeople(t?.trackerOwner),
        "Tracker Manager(s)": formatPeople(t?.trackerManager),
        "Legal Update Link": t?.legalUpdateLink || "",
    });

    const buildSummaryMatrixRows = () => {
        const rows = [];
        Object.entries(processedData).forEach(([lawArea, statuses]) => {
            complianceCategories.forEach((cat) => {
                const High = statuses?.[cat]?.High || 0;
                const Medium = statuses?.[cat]?.Medium || 0;
                const Low = statuses?.[cat]?.Low || 0;
                const UnsupportedRisk = statuses?.[cat]?.[UNSUPPORTED_VALUE] || 0;
                const Total = High + Medium + Low + UnsupportedRisk;
                if (Total > 0) {
                    rows.push({
                        "Law Area": lawArea,
                        Status: cat,
                        High,
                        Medium,
                        Low,
                        UnsupportedRisk,
                        Total,
                    });
                }
            });
        });
        return rows;
    };

    const buildGroupedSorted = (useFiltered) => {
        const src = useFiltered ? detailsRows : Object.values(lawAreaMap).flat();
        const groups = {};
        src.forEach((t) => {
            const la = t?.lawArea || "Unspecified";
            (groups[la] ||= []).push(t);
        });
        const orderedLawAreas = Object.keys(groups).sort((a, b) => a.localeCompare(b));
        return { groups, orderedLawAreas };
    };

    const makeFilename = (ext, filtered) => {
        const ts = new Date().toISOString().replace(/[:.]/g, "-");
        const scope = filtered ? "filtered" : "all";
        return `compliance_report_${scope}_${ts}.${ext}`;
    };

    const exportXLSX = (filtered) => {
        const { groups, orderedLawAreas } = buildGroupedSorted(filtered);
        const wb = XLSX.utils.book_new();
        const mainSheetName = filtered ? "Filtered (Grouped)" : "All (Grouped)";
        const lawAreaSheetNames = buildUniqueSheetNames(orderedLawAreas, [
            mainSheetName,
            "Summary Matrix",
            "Meta",
        ]);

        const flatRows = [];
        orderedLawAreas.forEach((la) => {
            groups[la].forEach((t) => flatRows.push(mapTrackerToRow(t)));
        });
        const wsMain = XLSX.utils.json_to_sheet(flatRows);
        XLSX.utils.book_append_sheet(wb, wsMain, mainSheetName);

        orderedLawAreas.forEach((la) => {
            const ws = XLSX.utils.json_to_sheet(groups[la].map(mapTrackerToRow));
            XLSX.utils.book_append_sheet(wb, ws, lawAreaSheetNames[la]);
        });

        const wsSummary = XLSX.utils.json_to_sheet(buildSummaryMatrixRows());
        XLSX.utils.book_append_sheet(wb, wsSummary, "Summary Matrix");

        const metaSheet = XLSX.utils.aoa_to_sheet([
            ["Companies", companies.join(", ") || "-"],
            ["Generated (IST)", formatDateTimeInIST()],
            [],
            ["KPI", "Value"],
            ["Total Open Items", kpis.total],
            ["High-Risk Items", kpis.high],
            ["High-Risk %", `${kpis.highPct}%`],
            ["Law Areas", kpis.affected],
        ]);
        XLSX.utils.book_append_sheet(wb, metaSheet, "Meta");

        XLSX.writeFile(wb, makeFilename("xlsx", filtered));
    };

    const exportPDF = (filtered) => {
        const { groups, orderedLawAreas } = buildGroupedSorted(filtered);
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });

        doc.setFontSize(16);
        doc.text("Compliance Report", 40, 40);
        doc.setFontSize(10);
        const metaLines = [
            `Companies: ${companies.join(", ") || "-"}`,
            `Generated: ${formatDateTimeInIST()} IST`,
            `Scope: ${filtered ? "Filtered selection" : "All items"}`,
        ];
        metaLines.forEach((ln, i) => doc.text(ln, 40, 60 + i * 14));

        const chartsY = 60 + metaLines.length * 14 + 16;
        const pageW = doc.internal.pageSize.getWidth();
        const left = 40;
        const gap = 24;
        const colW = (pageW - left * 2 - gap) / 2;
        const imgH = 220;

        const imgDonut = chartToPng(donutRef);
        const imgRiskShare = chartToPng(riskShareRef);
        const imgTopHigh = chartToPng(topHighRef);

        doc.setFontSize(12);
        doc.text("Charts", left, chartsY);
        let y = chartsY + 14;
        if (imgDonut) doc.addImage(imgDonut, "PNG", left, y, colW, imgH);
        if (imgRiskShare) doc.addImage(imgRiskShare, "PNG", left + colW + gap, y, colW, imgH);
        y += imgH + 18;
        if (imgTopHigh) doc.addImage(imgTopHigh, "PNG", left, y, pageW - left * 2, imgH);

        doc.addPage();
        let startY = 40;

        const columns = [
            "UIN",
            "Location",
            "Activity",
            "Tracker Details",
            "Dates (IST)",
            "Status / Risk",
            "Owners / Managers",
            "Legal Update Link",
        ];

        orderedLawAreas.forEach((la, idx) => {
            if (idx > 0 && startY > doc.internal.pageSize.getHeight() - 120) {
                doc.addPage();
                startY = 40;
            }
            doc.setFontSize(12);
            doc.text(`Law Area: ${la}`, 40, startY);
            startY += 8;

            const rows = groups[la].map(mapTrackerToRow);

            autoTable(doc, {
                startY,
                head: [columns],
                body: rows.map((row) => [
                    row.UIN,
                    row.Location,
                    row["Compliance Activity"],
                    [
                        `Description: ${row.Description || "-"}`,
                        `Category: ${row.Category || "-"}`,
                        `Act/Rule: ${row["Act or Rule"] || "-"}`,
                        `Periodicity: ${row.Periodicity || "-"}`,
                        `Consequence: ${row.Consequence || "-"}`,
                        `Source: ${row["Source URL"] || "-"}`,
                    ].join("\n"),
                    [
                        `Due: ${row["Due Date (IST)"] || "-"}`,
                        `Completed: ${row["Completion Date (IST)"] || "-"}`,
                    ].join("\n"),
                    [
                        `Status: ${row.Status || "-"}`,
                        `Risk: ${row.Risk || "-"}`,
                        `Proof submitted: ${row["Proof Submitted"]}`,
                        `Proof not required: ${row["Proof Not Required"]}`,
                    ].join("\n"),
                    [
                        `Owners: ${row["Tracker Owner(s)"] || "-"}`,
                        `Managers: ${row["Tracker Manager(s)"] || "-"}`,
                    ].join("\n"),
                    row["Legal Update Link"],
                ]),
                styles: { fontSize: 5.5, cellPadding: 2, overflow: "linebreak", valign: "top" },
                headStyles: { fillColor: [30, 136, 229], fontSize: 6, halign: "center" },
                columnStyles: {
                    0: { cellWidth: 42 },
                    1: { cellWidth: 44 },
                    2: { cellWidth: 68 },
                    3: { cellWidth: 170 },
                    4: { cellWidth: 68 },
                    5: { cellWidth: 74 },
                    6: { cellWidth: 100 },
                    7: { cellWidth: 145 },
                },
                didDrawPage: (data) => {
                    const str = `Page ${doc.internal.getNumberOfPages()}`;
                    doc.setFontSize(9);
                    doc.text(str, data.settings.margin.left, doc.internal.pageSize.getHeight() - 10);
                },
            });

            startY = (doc.lastAutoTable?.finalY || startY) + 16;
        });

        doc.save(makeFilename("pdf", filtered));
    };

    return (
        <Layout title="Risk Report">
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Compliance Report
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label="High" size="small" sx={{ bgcolor: "#fdecea", color: "#b71c1c" }} />
                        <Chip label="Medium" size="small" sx={{ bgcolor: "#fff3e0", color: "#e65100" }} />
                        <Chip label="Low" size="small" sx={{ bgcolor: "#e8f5e9", color: "#1b5e20" }} />
                        <Button variant="outlined" startIcon={<FileDownload />} onClick={openExportMenu} sx={{ ml: 1 }}>
                            Export
                        </Button>
                        <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={closeExportMenu}>
                            <MenuItem onClick={() => { exportXLSX(false); closeExportMenu(); }}>
                                <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
                                <ListItemText>Excel (.xlsx) — All items</ListItemText>
                            </MenuItem>
                            <MenuItem disabled={!detailsRows.length} onClick={() => { exportXLSX(true); closeExportMenu(); }}>
                                <ListItemIcon><GridOn fontSize="small" /></ListItemIcon>
                                <ListItemText>Excel (.xlsx) — Filtered (Details)</ListItemText>
                            </MenuItem>
                            <Divider sx={{ my: 0.5 }} />
                            <MenuItem onClick={() => { exportPDF(false); closeExportMenu(); }}>
                                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                                <ListItemText>PDF — All items (incl. charts)</ListItemText>
                            </MenuItem>
                            <MenuItem disabled={!detailsRows.length} onClick={() => { exportPDF(true); closeExportMenu(); }}>
                                <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                                <ListItemText>PDF — Filtered (incl. charts)</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Stack>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 8 }}>
                        <CircularProgress />
                    </Box>
                ) : !hasData ? (
                    <Paper variant="outlined" sx={{ ...panelSx }}>
                        <Typography variant="subtitle1" color="text.secondary" align="center">
                            No compliance data available yet.
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        <Grid container spacing={3} sx={{ mb: 1 }}>
                            {[
                                { label: "Total Open Items", value: kpis.total },
                                { label: "High-Risk Items", value: kpis.high },
                                { label: "High-Risk %", value: `${kpis.highPct}%` },
                                { label: "Law Areas", value: kpis.affected },
                            ].map((k, i) => (
                                <Grid key={i} item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ ...panelSx }}>
                                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                            {k.value}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {k.label}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={5} lg={4}>
                                <Paper variant="outlined" sx={{ ...panelSx, height: 420 }}>
                                    <Typography variant="h6" align="left" gutterBottom>
                                        Status Distribution
                                    </Typography>
                                    <Box sx={{ position: "relative", height: 330 }}>
                                        <Doughnut
                                            ref={donutRef}
                                            data={statusTotalsDonutData}
                                            options={{
                                                responsive: true,
                                                maintainAspectRatio: false,
                                                plugins: {
                                                    legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10 } },
                                                    title: { display: true, text: "All Open Items by Status" },
                                                    centerText: {
                                                        text: String(
                                                            (statusTotalsDonutData?.datasets?.[0]?.data || []).reduce((a, b) => a + b, 0)
                                                        ),
                                                    },
                                                    tooltip: {
                                                        callbacks: {
                                                            label: (ctx) => {
                                                                const value = ctx.raw ?? 0;
                                                                const total = (ctx.dataset.data || []).reduce((a, b) => a + b, 0) || 1;
                                                                const pct = Math.round((value / total) * 100);
                                                                return `${ctx.label}: ${value} (${pct}%)`;
                                                            },
                                                        },
                                                    },
                                                },
                                                cutout: "65%",
                                            }}
                                            onClick={(_e, els) => {
                                                if (!els?.length) return;
                                                const { index } = els[0];
                                                const status = statusTotalsDonutData.labels[index];
                                                setSelectionFromLabel(null, status, null);
                                            }}
                                        />
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={7} lg={8}>
                                <Stack spacing={3}>
                                    <Paper variant="outlined" sx={{ ...panelSx, height: 280 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Risk Share by Law Area
                                        </Typography>
                                        <Box sx={{ position: "relative", height: 200 }}>
                                            <Bar
                                                ref={riskShareRef}
                                                data={riskShareChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    plugins: {
                                                        legend: { position: "bottom", labels: { usePointStyle: true, boxWidth: 10, boxHeight: 10 } },
                                                        title: { display: true, text: "Risk Composition (100%)" },
                                                        tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%` } },
                                                    },
                                                    scales: {
                                                        x: { stacked: true },
                                                        y: { stacked: true, beginAtZero: true, max: 100, ticks: { callback: (v) => `${v}%` } },
                                                    },
                                                }}
                                                onClick={(_e, els) => {
                                                    if (!els?.length) return;
                                                    const { datasetIndex, index } = els[0];
                                                    const lawArea = riskShareChartData.labels[index];
                                                    const risk = (riskShareChartData.datasets[datasetIndex].label || "").replace(" Risk", "");
                                                    setSelectionFromLabel(lawArea, null, risk);
                                                }}
                                            />
                                        </Box>
                                    </Paper>

                                    <Paper variant="outlined" sx={{ ...panelSx, height: 320 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Top High-Risk Law Areas
                                        </Typography>
                                        <Box sx={{ position: "relative", height: 240 }}>
                                            <Bar
                                                ref={topHighRef}
                                                data={topHighRiskChartData}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    indexAxis: "y",
                                                    plugins: {
                                                        legend: { display: false },
                                                        title: { display: true, text: "High Risk Items (Top 8)" },
                                                    },
                                                    scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
                                                }}
                                                onClick={(_e, els) => {
                                                    if (!els?.length) return;
                                                    const { index } = els[0];
                                                    const lawArea = topHighRiskChartData.labels[index];
                                                    setSelectionFromLabel(lawArea, null, "High");
                                                }}
                                            />
                                        </Box>
                                    </Paper>
                                </Stack>
                            </Grid>
                        </Grid>

                        <Paper variant="outlined" sx={{ ...panelSx, mt: 3, overflow: "hidden" }}>
                            <Typography variant="h6" sx={{ mb: 1.5 }}>
                                Open Items Matrix
                            </Typography>
                            <TableContainer sx={{ maxHeight: { xs: 360, md: "50vh" } }}>
                                <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                rowSpan={2}
                                                align="center"
                                                sx={{
                                                    fontWeight: "bold",
                                                    minWidth: 200,
                                                    position: "sticky",
                                                    left: 0,
                                                    zIndex: 3,
                                                    bgcolor: "background.paper",
                                                    borderRight: "1px solid",
                                                    borderColor: "divider",
                                                }}
                                            >
                                                Law Area
                                            </TableCell>
                                            {riskLevels.map((risk) => (
                                                <TableCell
                                                    key={`risk-${risk}`}
                                                    align="center"
                                                    colSpan={complianceCategories.length}
                                                    sx={{ fontWeight: 700, borderLeft: "1px solid", borderColor: "divider" }}
                                                >
                                                    {risk}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                        <TableRow>
                                            {riskLevels.map((risk) =>
                                                complianceCategories.map((category) => (
                                                    <TableCell key={`hdr-${risk}-${category}`} align="center" sx={{ fontWeight: 600, minWidth: 140 }}>
                                                        {category}
                                                    </TableCell>
                                                ))
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.keys(processedData).map((lawArea) => (
                                            <TableRow hover key={lawArea}>
                                                <TableCell
                                                    align="left"
                                                    sx={{
                                                        fontWeight: 600,
                                                        position: "sticky",
                                                        left: 0,
                                                        zIndex: 2,
                                                        bgcolor: "background.paper",
                                                        borderRight: "1px solid",
                                                        borderColor: "divider",
                                                    }}
                                                >
                                                    {lawArea}
                                                </TableCell>

                                                {riskLevels.map((risk) =>
                                                    complianceCategories.map((category) => {
                                                        const count = processedData[lawArea]?.[category]?.[risk] || 0;
                                                        const selected = isSelected(lawArea, category, risk);
                                                        const alpha = Math.min(count / 10, 0.25);
                                                        const bg =
                                                            risk === "High"
                                                                ? `rgba(229,57,53,${alpha})`
                                                                : risk === "Medium"
                                                                    ? `rgba(251,140,0,${alpha})`
                                                                    : `rgba(67,160,71,${alpha})`;
                                                        return (
                                                            <Tooltip
                                                                key={`${lawArea}-${risk}-${category}`}
                                                                title={`${lawArea} • ${risk} • ${category} — ${count}`}
                                                                arrow
                                                            >
                                                                <TableCell
                                                                    align="center"
                                                                    onClick={() => handleCellClick(lawArea, category, risk)}
                                                                    sx={(theme) => ({
                                                                        cursor: "pointer",
                                                                        whiteSpace: "nowrap",
                                                                        bgcolor: selected ? theme.palette.action.selected : bg,
                                                                        "&:hover": { bgcolor: theme.palette.action.hover },
                                                                        borderLeft: "1px solid",
                                                                        borderColor: "divider",
                                                                        fontWeight: count > 0 ? 600 : 400,
                                                                        color: count > 0 ? "text.primary" : "text.secondary",
                                                                    })}
                                                                >
                                                                    {count}
                                                                </TableCell>
                                                            </Tooltip>
                                                        );
                                                    })
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {(selectedCell.lawArea || selectedCell.status || selectedCell.risk) && (
                            <Paper variant="outlined" sx={{ ...panelSx, mt: 3 }}>
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "flex-start", sm: "center" }}
                                    justifyContent="space-between"
                                >
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="h6">Details</Typography>
                                        {selectedCell.lawArea && (
                                            <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                                                — {selectedCell.lawArea}
                                            </Typography>
                                        )}
                                        {selectedCell.status && (
                                            <Chip label={selectedCell.status} size="small" color={statusColors[selectedCell.status] || "default"} />
                                        )}
                                        {selectedCell.risk && (
                                            <Chip
                                                label={selectedCell.risk}
                                                size="small"
                                                sx={{
                                                    bgcolor:
                                                        selectedCell.risk === "High"
                                                            ? "#fdecea"
                                                            : selectedCell.risk === "Medium"
                                                                ? "#fff3e0"
                                                                : "#e8f5e9",
                                                }}
                                            />
                                        )}
                                    </Stack>

                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {detailsRows.length} item{detailsRows.length === 1 ? "" : "s"}
                                        </Typography>
                                        <Button size="small" startIcon={<RestartAlt />} onClick={clearSelection}>
                                            Reset filters
                                        </Button>
                                    </Stack>
                                </Stack>

                                <Divider sx={{ my: 1.5 }} />

                                <TableContainer sx={{ maxHeight: 440 }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>UIN</TableCell>
                                                <TableCell>Location</TableCell>
                                                <TableCell>Compliance Activity</TableCell>
                                                <TableCell>Risk</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell align="center">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {detailsRows
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((t, idx) => (
                                                    <TableRow
                                                        hover
                                                        key={`${t?.uniqueIdentifier || "row"}-${idx}`}
                                                        sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}
                                                    >
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.uniqueIdentifier || "-"}</TableCell>
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{t?.location || "-"}</TableCell>
                                                        <TableCell>{t?.complianceActivity || "-"}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={normalizeRisk(t?.risk)}
                                                                size="small"
                                                                sx={{
                                                                    bgcolor:
                                                                        normalizeRisk(t?.risk) === "High"
                                                                            ? "#fdecea"
                                                                            : normalizeRisk(t?.risk) === "Medium"
                                                                                ? "#fff3e0"
                                                                                : "#e8f5e9",
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={canonicalStatus(t?.status) || "-"}
                                                                size="small"
                                                                color={statusColors[canonicalStatus(t?.status) || ""] || "default"}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip title="Open details">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        navigate(`/Edit/${encodeURIComponent(t?.uniqueIdentifier || "")}`)
                                                                    }
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <TablePagination
                                    component="div"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    count={detailsRows.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={(_, p) => setPage(p)}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                />
                            </Paper>
                        )}
                    </>
                )}
            </Container>
        </Layout>
    );


};

export default ComplianceReportPage;